// src/utils/statsUtils.js
import GLOBALS from '../config/globals';

export const calculateBattingStats = rollResults => {
    const stats = {
        h1: 0,
        h2: 0,
        h3: 0,
        hr: 0,
        so: 0,
        oo: 0,
        bb: 0,
        ab: 0,
        h: 0,
        tb: 0, // Total bases
        pa: 0, // Plate appearances
    };

    rollResults.forEach(result => {
        stats.pa += 1;
        switch (result) {
            case 'h1':
                stats.h1 += 1;
                stats.h += 1;
                stats.tb += 1;
                stats.ab += 1;
                break;
            case 'h2':
                stats.h2 += 1;
                stats.h += 1;
                stats.tb += 2;
                stats.ab += 1;
                break;
            case 'h3':
                stats.h3 += 1;
                stats.h += 1;
                stats.tb += 3;
                stats.ab += 1;
                break;
            case 'hr':
                stats.hr += 1;
                stats.h += 1;
                stats.tb += 4;
                stats.ab += 1;
                break;
            case 'so':
                stats.so += 1;
                stats.ab += 1;
                break;
            case 'oo':
                stats.oo += 1;
                stats.ab += 1;
                break;
            case 'bb':
                stats.bb += 1;
                break;
            default:
                stats.oo += 1;
                stats.ab += 1;
                break;
        }
    });

    const average = stats.ab > 0 ? (stats.h / stats.ab).toFixed(3) : '0.000';
    const obp = stats.pa > 0 ? ((stats.h + stats.bb) / stats.pa).toFixed(3) : '0.000';
    const slugging = stats.ab > 0 ? (stats.tb / stats.ab).toFixed(3) : '0.000';
    const ops = (parseFloat(obp) + parseFloat(slugging)).toFixed(3);
    // O3S: weighted stat where OBP is 3x as valuable as SLG
    const o3s = ((3 * parseFloat(obp) + parseFloat(slugging)) / 2).toFixed(3);

    return {
        ...stats,
        average,
        obp,
        slugging,
        ops,
        o3s,
    };
};

/**
 * Simulate pitching stats from roll results by tracking innings.
 * Counts outs to form innings, advances runners on hits/walks, tracks earned runs.
 * Returns per-9 rate stats (ERA, K/9, BB/9, H/9, HR/9, WHIP) plus IP.
 */
export const calculatePitchingStats = rollResults => {
    let outs = 0;
    let runs = 0;
    let hits = 0;
    let walks = 0;
    let strikeouts = 0;
    let homeRuns = 0;
    let bases = [false, false, false]; // 1st, 2nd, 3rd

    const scoreRunner = () => { runs++; };

    const advanceRunners = numBases => {
        // Advance all runners by numBases, score anyone pushed past 3rd
        for (let b = 2; b >= 0; b--) {
            if (!bases[b]) continue;
            bases[b] = false;
            const newBase = b + numBases;
            if (newBase >= 3) {
                scoreRunner();
            } else {
                bases[newBase] = true;
            }
        }
        // Place batter
        if (numBases >= 4) {
            scoreRunner();
        } else {
            bases[numBases - 1] = true;
        }
    };

    const clearBases = () => {
        bases = [false, false, false];
    };

    rollResults.forEach(result => {
        switch (result) {
            case 'h1':
                hits++;
                advanceRunners(1);
                break;
            case 'h2':
                hits++;
                advanceRunners(2);
                break;
            case 'h3':
                hits++;
                advanceRunners(3);
                break;
            case 'hr':
                hits++;
                homeRuns++;
                advanceRunners(4);
                break;
            case 'bb':
                walks++;
                // Walk: advance forced runners, batter to 1st
                if (bases[0] && bases[1] && bases[2]) { scoreRunner(); bases[2] = true; }
                else if (bases[0] && bases[1]) { bases[2] = true; }
                else if (bases[0]) { bases[1] = true; }
                bases[0] = true;
                break;
            case 'so':
                strikeouts++;
                outs++;
                break;
            default: // oo
                outs++;
                break;
        }
        // End of inning: clear bases every 3 outs
        if (outs % 3 === 0 && outs > 0) {
            clearBases();
        }
    });

    const ip = outs / 3;
    const per9 = stat => ip > 0 ? ((stat / ip) * 9).toFixed(2) : '0.00';

    return {
        ip: ip.toFixed(1),
        era: per9(runs),
        whip: ip > 0 ? ((hits + walks) / ip).toFixed(2) : '0.00',
        k9: per9(strikeouts),
        bb9: per9(walks),
        h9: per9(hits),
        hr9: per9(homeRuns),
        runs,
        hits,
        walks,
        strikeouts,
        homeRuns,
        outs,
    };
};

export const rollDiceForCard = (card, numRolls) => {
    const results = [];
    const rollTwoDice = () => {
        const die1 = getRandomInt(1, 6);
        const die2 = getRandomInt(1, 6);
        return die1 + die2;
    };
    for (let i = 0; i < numRolls; i++) {
        const roll = rollTwoDice();
        const result = card[`d${roll}`];
        if (result === 'xb') {
            const xbRoll = rollTwoDice();
            results.push(card[`xd${xbRoll}`]);
        } else {
            results.push(result);
        }
    }
    return results;
};

export const rollDiceForCardVs = (battingCard, pitchingCard, numRolls) => {
    const results = [];
    const preResults = [];

    const rollTwoDice = () => {
        const die1 = getRandomInt(1, 6);
        const die2 = getRandomInt(1, 6);
        return die1 + die2;
    };

    for (let i = 0; i < numRolls; i++) {
        const roll = rollTwoDice();
        let bResult = battingCard[`d${roll}`];
        const batterResultReroll = rollTwoDice();
        const pRoll = rollTwoDice();
        const pResult = pitchingCard[`pd${pRoll}`];
        if (bResult === 'xb') {
            const xbRoll = rollTwoDice();
            bResult = battingCard[`xd${xbRoll}`];
        }
        preResults.push(bResult);
        results.push(getFinalResult(bResult, batterResultReroll, pResult, pitchingCard.rating ?? 0));
    }

    return { results, preResults };
};

export const getFinalResult = (batterResult, batterResultReroll, pitcherResult, rating = 0) => {
    if (!pitcherResult) {
        return batterResult;
    }

    const [action, direction] = pitcherResult.split('-');
    switch (action) {
        case 'h9':
            return affectH(batterResult, direction, rating);
        case 'bb9':
            return affectbb(batterResult, direction);
        case 'hr9':
            return affecthr(batterResult, direction, rating);
        case 'k9':
            return affectK(batterResult, direction, batterResultReroll);
        default:
            return batterResult;
    }
};

export const affectbb = (preresult, direction) => {
    if (direction === 'h') {
        if ([GLOBALS.SO].includes(preresult)) {
            return GLOBALS.BB;
        }
    } else {
        if ([GLOBALS.BB].includes(preresult)) {
            return GLOBALS.OO; //SO OR OO?
        }
    }
    return preresult;
};

export const affectH = (preresult, direction, rating = 0) => {
    if (direction === 'h') {
        if (preresult === GLOBALS.OO) {
            return GLOBALS.H1;
        }
    } else {
        if (preresult === GLOBALS.H1) {
            return GLOBALS.OO;
        }
        // XBH suppression: better pitchers (higher rating) fire more often.
        // P = 0.5 × (rating/100), calibrated via K-sweep (K=0.5 chosen for gameplay).
        const suppressProbability = 0.5 * (rating / 100);
        if (preresult === GLOBALS.H2 && Math.random() < suppressProbability) {
            return GLOBALS.H1;
        }
        if (preresult === GLOBALS.HR && Math.random() < suppressProbability) {
            return GLOBALS.H2;
        }
    }
    return preresult;
};

export const affectK = (preresult, direction, batterResultReroll) => {
    if (direction === 'h') {
        if (preresult === GLOBALS.OO && Math.random() > GLOBALS.K_AFFECT_PCT) {
            return GLOBALS.SO;
        }
        if (preresult === GLOBALS.BB && Math.random() > GLOBALS.K_AFFECT_PCT) {
            //we make need to tweak this
            return GLOBALS.SO;
        }
    } else {
        if (preresult === GLOBALS.SO) {
            return batterResultReroll;
        }
    }
    return preresult;
};

export const affecthr = (preresult, direction, rating = 0) => {
    if (direction === 'h') {
        if (preresult === GLOBALS.H2) {
            return GLOBALS.HR;
        }
        if (preresult === GLOBALS.H1) {
            return GLOBALS.H2;
        }
    } else {
        if (preresult === GLOBALS.HR) {
            return GLOBALS.H2;
        }
        if (preresult === GLOBALS.H2) {
            return GLOBALS.H1;
        }
        // Single suppression: better pitchers (higher rating) fire more often.
        // P = 0.5 × (rating/100), same calibration constant as h9-l XBH suppression.
        if (preresult === GLOBALS.H1 && Math.random() < 0.5 * (rating / 100)) {
            return GLOBALS.OO;
        }
    }
    return preresult;
};

const getRandomInt = (min, max) => {
    return Math.floor(Math.random() * (max - min + 1)) + min;
};

export const combineBattingRecords = battings => {
    if (!battings || battings.length === 0) return null;

    return battings.reduce(
        (acc, curr) => {
            acc.AB += curr.AB ?? 0;
            acc.G += curr.G ?? 0;
            acc.H += curr.H ?? 0;
            acc['2B'] += curr['2B'] ?? 0;
            acc['3B'] += curr['3B'] ?? 0;
            acc.HR += curr.HR ?? 0;
            acc.R += curr.R ?? 0;
            acc.RBI += curr.RBI ?? 0;
            acc.SB += curr.SB ?? 0;
            acc.CS += curr.CS ?? 0;
            acc.BB += curr.BB ?? 0;
            acc.SO += curr.SO ?? 0;
            acc.IBB += curr.IBB ?? 0;
            acc.HBP += curr.HBP ?? 0;
            acc.SH += curr.SH ?? 0;
            acc.SF += curr.SF ?? 0;
            acc.GIDP += curr.GIDP ?? 0;
            return acc;
        },
        {
            AB: 0,
            G: 0,
            H: 0,
            '2B': 0,
            '3B': 0,
            HR: 0,
            R: 0,
            RBI: 0,
            SB: 0,
            CS: 0,
            BB: 0,
            SO: 0,
            IBB: 0,
            HBP: 0,
            SH: 0,
            SF: 0,
            GIDP: 0,
        }
    );
};

export const combinePitchingRecords = pitchings => {
    if (!pitchings || pitchings.length === 0) return null;

    return pitchings.reduce(
        (acc, curr) => {
            acc.IPouts += curr.IPouts ?? 0;
            acc.ER += curr.ER ?? 0;
            acc.BB += curr.BB ?? 0;
            acc.H += curr.H ?? 0;
            acc.SO += curr.SO ?? 0;
            acc.W += curr.W ?? 0;
            acc.L += curr.L ?? 0;
            acc.G += curr.G ?? 0;
            acc.GS += curr.GS ?? 0;
            acc.CG += curr.CG ?? 0;
            acc.SHO += curr.SHO ?? 0;
            acc.SV += curr.SV ?? 0;
            acc.HR += curr.HR ?? 0;
            return acc;
        },
        {
            IPouts: 0,
            ER: 0,
            BB: 0,
            H: 0,
            SO: 0,
            W: 0,
            L: 0,
            G: 0,
            GS: 0,
            CG: 0,
            SHO: 0,
            SV: 0,
            HR: 0,
        }
    );
};

export const formatAverage = avg => {
    let avgString = '' + avg.toFixed(3);

    // If the first two characters are "0.", remove the leading zero
    if (avgString.startsWith('0.')) {
        avgString = avgString.slice(1);
    }

    return avgString;
};
